import { Button, Input, Loading } from 'react-daisyui';
import './WabTemplatesVariablesAction.styles.scss';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import { ChatEnum } from '../../../../Chat.enum.ts';
import { useTranslation } from 'react-i18next';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/16/solid';
import useWabTemplatesVariablesAction from 'src/components/Chat/components/MessageBar/components/WabTemplatesVariablesAction/WabTemplatesVariablesAction.hook.tsx';
import {
  acceptForMediaKind,
  isTemplateMediaPreviewable,
} from 'src/utils/templateMedia.ts';
import InactiveConversationModal from '../WabTemplatesAction/Components/InactiveConversationModal/InactiveConversationModal.component.tsx';
import BlockedLeadModal from '../WabTemplatesAction/Components/BlockedLeadModal/BlockedLeadModal.component';
import { useTopUpRoleGate } from 'src/pages/Wallet/hooks/useTopUpRoleGate';
import { useTopUpModal } from 'src/pages/Wallet/hooks/useTopUpModal';
import ComposerNoBalanceBar from 'src/pages/Wallet/components/ComposerNoBalanceBar/ComposerNoBalanceBar.component';

export default function WabTemplatesVariablesAction() {
  const { t } = useTranslation('chat/chat');
  const { wapiVariables, setSelectedAction } = useMessageBarContext();
  const {
    paramValues,
    predefinedKeys,
    hasMissingPredefinedValues,
    imageUrl,
    loadingImage,
    blockImgText,
    imageRef,
    isDisabled,
    expectedMediaKind,
    mediaLimits,
    headerNeedsMedia,
    isMediaUrlEmpty,
    isUrlFormatInvalid,
    loading,
    couponValues,
    handleChange,
    handleCouponChange,
    handleUrlChange,
    uploadImage,
    renderHeader,
    renderTemplate,
    handleSendTemplate,
    isInactiveModalOpen,
    closeInactiveModal,
    isBlockedLeadModalOpen,
    closeBlockedLeadModal,
    confirmInactiveModal,
    isBlocked,
    costLabel,
    shortfall,
  } = useWabTemplatesVariablesAction();
  const { openOneOffWithAmount, modal: topUpModal } = useTopUpModal();
  // Sólo Owner/Admin recargan (DS-03 §3, mismo criterio que DS-16/DS-17;
  // RET-2115: único lugar que decide, `useTopUpRoleGate`). El componente de la
  // barra no lee el rol, lo decide quien la monta.
  const { canTopUp: canTopUpBalance } = useTopUpRoleGate();

  const headerContent = renderHeader();

  return (
    <div className="WabTemplatesVariablesAction">
      <div className="WabTemplatesVariablesAction__Left">
        <h2 className="WabTemplatesVariablesAction__Left__Title">
          {predefinedKeys.size > 0 &&
          predefinedKeys.size < (wapiVariables?.parameters.length ?? 0)
            ? t('wabTemplatesVariablesAction.titleMixed')
            : predefinedKeys.size > 0
              ? t('wabTemplatesVariablesAction.titleAutomatic')
              : t('wabTemplatesVariablesAction.title')}
        </h2>
        {/*
          BUG-866: el bloque de adjuntar entero — label, input de URL,
          <input type=file>, botón, guía y error inline — se pinta según el
          `format` declarado del HEADER, no según la forma de la URL de
          ejemplo. Con un handle de upload opaco por ejemplo este bloque no se
          renderizaba, así que el modal abría sin ninguna forma de adjuntar y
          el envío salía sin `media`. El predicado se calcula una sola vez en
          el hook y lo comparten los siete sitios (KTD7).
        */}
        {headerNeedsMedia && (
          <>
            <label>
              {t(`wabTemplatesVariablesAction.urlLabel.${expectedMediaKind}`)}
            </label>
            <Input
              className="w-full"
              placeholder={t(
                `wabTemplatesVariablesAction.urlLabel.${expectedMediaKind}`,
              )}
              type="text"
              value={imageUrl}
              disabled={blockImgText}
              onChange={(e) => handleUrlChange(e.target.value)}
            />
            {loadingImage ? (
              <Loading size="xs" color="primary" />
            ) : (
              <>
                <input
                  ref={imageRef}
                  onChange={uploadImage}
                  className="hidden"
                  type="file"
                  accept={acceptForMediaKind(expectedMediaKind)}
                />
                <button
                  type="button"
                  onClick={() => imageRef.current?.click()}
                  className="WabTemplatesVariablesAction__Button"
                >
                  {/* A PDF or a video in an <img> renders broken (BUG-1039). */}
                  {imageUrl &&
                  isTemplateMediaPreviewable(expectedMediaKind, imageUrl) ? (
                    <img
                      src={imageUrl}
                      alt={t('wabTemplatesVariablesAction.previewAlt')}
                      className="WabTemplatesVariablesAction__Src"
                    />
                  ) : (
                    <PhotoIcon className="WabTemplatesVariablesAction__Icon" />
                  )}

                  <p> {t('wabTemplatesVariablesAction.files')}</p>
                </button>
              </>
            )}
            {isMediaUrlEmpty && (
              <p className="text-xs text-gray-600 mt-1">
                {t(
                  `wabTemplatesVariablesAction.mediaGuidance.${expectedMediaKind}`,
                  mediaLimits,
                )}
              </p>
            )}
            {isUrlFormatInvalid && (
              <p role="alert" className="text-error text-xs mt-1">
                {t(
                  `wabTemplatesVariablesAction.invalidUrlFormat.${expectedMediaKind}`,
                )}
              </p>
            )}
          </>
        )}
        {wapiVariables?.parameters.map((param) => {
          const isPredefined = predefinedKeys.has(param.name);
          return (
            <div key={param.name} className="mb-2">
              <label>{`{{${param.name}}}`}</label>
              <Input
                className="w-full"
                placeholder={`${t('wabTemplatesVariablesAction.input.placeholder')} ${param.name}`}
                type="text"
                value={paramValues[param.name] ?? ''}
                disabled={isPredefined}
                onChange={(e) => handleChange(param.name, e.target.value)}
              />
            </div>
          );
        })}
        {wapiVariables?.copyCodeButtons?.map((btn) => (
          <div key={`copy-code-${btn.index}`} className="mb-2">
            <label htmlFor={`copy-code-input-${btn.index}`}>{btn.text}</label>
            <Input
              id={`copy-code-input-${btn.index}`}
              className="w-full"
              placeholder={t(
                'wabTemplatesVariablesAction.input.couponPlaceholder',
              )}
              type="text"
              value={couponValues[btn.index] ?? ''}
              disabled={loading}
              onChange={(e) => handleCouponChange(btn.index, e.target.value)}
            />
          </div>
        ))}
      </div>
      <div className="WabTemplatesVariablesAction__Right">
        <div className="WabTemplatesVariablesAction__Right__Close">
          <XMarkIcon
            onClick={() => setSelectedAction(ChatEnum.WABTEMPLATES)}
            className="WabTemplatesVariablesAction__Right__Close__Icon"
          />
        </div>
        <h4 className="WabTemplatesVariablesAction__Right__Title">
          {t('wabTemplatesVariablesAction.subtitle')}
        </h4>
        {headerContent && (
          <pre className="WabTemplatesVariablesAction__Right__Content__Header">
            {headerContent}
          </pre>
        )}
        <pre className="WabTemplatesVariablesAction__Right__Content">
          {renderTemplate()}
        </pre>
        <Button
          className="WabTemplatesVariablesAction__Right__Button"
          onClick={handleSendTemplate}
          /*
            `isBlocked` NO va en `disabled`, va en `aria-disabled`.

            Un botón `disabled` no despacha el clic, y los cuatro `setIsBlocked`
            viven dentro de `checkBalance`: impedir el clic impide que el
            veredicto se recalcule. El cliente recargaba desde el CTA de la
            propia barra, volvía, y el botón seguía muerto hasta salir de la
            acción o cambiar de chat — el mismo callejón que `sendTemplate()`
            cerró en el camino sin variables, reintroducido aquí por una línea.

            `aria-disabled` atenúa igual (DS-03: «el botón de enviar se atenúa»)
            y deja pasar el intento, que es lo que permite que la cotización
            vuelva a decidir. Con el saldo todavía corto vuelve a bloquear, así
            que no se debilita nada.

            `isDisabled` SÍ sigue en `disabled`: eso es el formulario incompleto
            (variables vacías, media inválida), que no caduca solo — lo arregla
            el cliente escribiendo, y ahí un botón muerto es la señal correcta.
          */
          disabled={isDisabled}
          aria-disabled={isBlocked ? 'true' : undefined}
          loading={loading}
        >
          {t('wabTemplatesVariablesAction.button.text')}
        </Button>
        {hasMissingPredefinedValues && (
          <p className="text-error text-xs mt-2">
            {t('wabTemplatesVariablesAction.missingPredefinedValues')}
          </p>
        )}
        {isBlocked && (
          <ComposerNoBalanceBar
            costLabel={costLabel}
            onTopUp={
              canTopUpBalance && shortfall
                ? () => openOneOffWithAmount(shortfall)
                : undefined
            }
          />
        )}
      </div>
      {topUpModal}
      <InactiveConversationModal
        isOpen={isInactiveModalOpen}
        onClose={closeInactiveModal}
        onConfirm={confirmInactiveModal}
      />
      <BlockedLeadModal
        isOpen={isBlockedLeadModalOpen}
        onClose={closeBlockedLeadModal}
      />
    </div>
  );
}
